"use client";
import { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import {
  faPhone,
  faCommentDots,
  faBell,
} from "@fortawesome/free-solid-svg-icons";
import { differenceInDays, parseISO, format } from "date-fns";
import Spinner from "../spinner";

import styles from "../../styles/patient-page-modules/Contact.module.css";
import RingCentralWebSocket from "../../components/RingCentralWebSocket";
import toast from "react-hot-toast";
import Charts from "../case-manager/reading-reviews/Charts";
import Modal from "../shared-modules/Modal";
import { getOutOfRangeReadings } from "../../services/admin-case-manager/readings";
import {
  getPatientGeneralDetails,
  getPatientReadings,
} from "../../services/patient/details";
import { useQuery } from "@tanstack/react-query";

const ALERT_CONFIG = {
  TYPES: {
    OUT_OF_RANGE: "out_of_range",
    REMINDER: "reminder",
    MONITORING_TIME: "monitoring_time",
    UNSEEN_READINGS: "unseen_readings",
  },
  SEVERITY: {
    CRITICAL: "critical",
    HIGH: "high",
    MEDIUM: "medium",
    LOW: "low",
  },
  THRESHOLDS: {
    MIN_RPM_SECONDS: 1200,
    READING_REMINDER_DAYS: 3,
  },
};

const Contact = ({ isOpen, onClose, patientContacts }) => {
  const [state, setState] = useState({
    isModalOpen: false,
    alertModalOpen: false,
    outOfRangeModalOpen: false,
    phoneNumber: null,
    alerts: [],
    outOfRangeReadings: [],
    patient: null,
    isLoading: false,
    hasMonitoringTimeAlert: false,
  });

  useEffect(() => {
    document.body.style.overflow =
      state.isModalOpen || state.alertModalOpen ? "hidden" : "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [state.isModalOpen, state.alertModalOpen]);

  const patientId = sessionStorage.getItem("_pt_id");
  const {
    data: patientDetails,
    error,
    isLoading,
    refetch,
  } = useQuery(
    ["patient-general-details", patientId],
    () => getPatientGeneralDetails(patientId),
    { enabled: !!patientId }
  );

  const updateState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const formatDate = useCallback((dateString) => {
    if (!dateString) return "Unknown date";

    try {
      const date = parseISO(dateString);
      return isNaN(date.getTime())
        ? "Unknown date"
        : format(date, "MMM d, yyyy 'at' h:mm a");
    } catch {
      return "Unknown date";
    }
  }, []);

  const calculateDaysSince = useCallback((dateString) => {
    if (!dateString) return null;

    try {
      const date = parseISO(dateString);
      if (isNaN(date.getTime())) return null;

      const daysDiff = differenceInDays(new Date(), date);
      return Math.max(0, daysDiff);
    } catch {
      return null;
    }
  }, []);

  const getPrimaryPhoneNumber = useCallback(() => {
    if (!patientContacts?.length) return null;

    const primaryContact = patientContacts.find(
      (contact) =>
        contact.label?.toLowerCase().includes("primary") ||
        contact.label?.toLowerCase().includes("main") ||
        contact.is_primary
    );

    return primaryContact?.number || patientContacts[0].number || patientDetails?.user?.primary_phone;
  }, [patientContacts, patientDetails]);

  const createAlert = useCallback(
    (type, severity, message, additionalData = {}) => {
      const alertTitles = {
        [ALERT_CONFIG.TYPES.OUT_OF_RANGE]: "Out Of Range",
        [ALERT_CONFIG.TYPES.REMINDER]: "Reading Reminder",
        [ALERT_CONFIG.TYPES.MONITORING_TIME]: "Monitoring Time Alert",
        [ALERT_CONFIG.TYPES.UNSEEN_READINGS]: "Unseen Readings",
      };

      return {
        id: `${type}-${Date.now()}`,
        type,
        severity,
        title: alertTitles[type] || "Alert",
        message,
        date: new Date().toISOString(),
        // formattedDate: format(new Date(), "MMM d, yyyy 'at' h:mm a"),
        // ...additionalData,
      };
    },
    []
  );

  const createOutOfRangeAlert = useCallback(
    (record) => {
      const daysSince = calculateDaysSince(record.created_at);
      let message;

      if (record.reading_type === "blood_pressure") {
        message = `Out of range Blood Pressure: ${record.systolic}/${record.diastolic} mm Hg`;
        if (record.heart_rate) message += ` and ${record.heart_rate} bpm`;
        message += ` recorded ${daysSince} days ago.`;
      } else {
        message = `Out of range ${record.reading_type_display || record.reading_type
          } detected on ${formatDate(record.created_at)}.`;
      }

      return createAlert(
        ALERT_CONFIG.TYPES.OUT_OF_RANGE,
        ALERT_CONFIG.SEVERITY.CRITICAL,
        message,
        {
          readingType: record.reading_type,
          date: record.created_at,
          formattedDate: formatDate(record.created_at),
        }
      );
    },
    [createAlert, calculateDaysSince, formatDate]
  );

  const createReadingReminderAlert = useCallback(
    (lastReadingDate, daysSince) => {
      const daysText = typeof daysSince === "number" ? daysSince : "many";
      const lastReadingText = lastReadingDate
        ? formatDate(lastReadingDate)
        : "No readings found";
      const message = `Patient hasn't submitted readings for ${daysText} days. Last reading: ${lastReadingText}.`;

      return createAlert(
        ALERT_CONFIG.TYPES.REMINDER,
        ALERT_CONFIG.SEVERITY.HIGH,
        message
      );
    },
    [createAlert, formatDate]
  );

  const createMonitoringTimeAlert = useCallback(
    (monitoringTime) => {
      const message = `Patient has only ${monitoringTime.formatted_rpm_time} of RPM monitoring time this month.`;
      return createAlert(
        ALERT_CONFIG.TYPES.MONITORING_TIME,
        ALERT_CONFIG.SEVERITY.MEDIUM,
        message
      );
    },
    [createAlert]
  );

  const createUnseenReadingsAlert = useCallback(() => {
    const message = "Patient has unseen readings that need to be reviewed.";
    return createAlert(
      ALERT_CONFIG.TYPES.UNSEEN_READINGS,
      ALERT_CONFIG.SEVERITY.LOW,
      message
    );
  }, [createAlert]);

  const checkReadingReminder = useCallback(
    (patientData) => {
      try {
        // Check if last_reading_record exists
        if (patientData.last_reading_record && patientData.last_reading_record.recorded_at) {
          const daysSinceLastReading = calculateDaysSince(patientData.last_reading_record.recorded_at);

          if (daysSinceLastReading !== null && daysSinceLastReading > 3) {
            return createReadingReminderAlert(
              patientData.last_reading_record.recorded_at,
              daysSinceLastReading
            );
          }
        } else {
          // last_reading_record is null, check created_at
          const daysSinceJoining = calculateDaysSince(patientData.created_at);

          if (daysSinceJoining !== null && daysSinceJoining > 3) {
            const message = `Patient joined ${daysSinceJoining} days ago but has not submitted any reading yet.`;
            return createAlert(
              ALERT_CONFIG.TYPES.REMINDER,
              ALERT_CONFIG.SEVERITY.HIGH,
              message
            );
          } else {
            // Patient joined 3 days ago or less, still show reminder
            const message = "Patient is new in the portal.";
            return createAlert(
              ALERT_CONFIG.TYPES.REMINDER,
              ALERT_CONFIG.SEVERITY.HIGH,
              message
            );
          }
        }

        return null;
      } catch (error) {
        return createAlert(
          ALERT_CONFIG.TYPES.REMINDER,
          ALERT_CONFIG.SEVERITY.MEDIUM,
          "Patient has no readings available."
        );
      }
    },
    [createReadingReminderAlert, calculateDaysSince, createAlert]
  );

  const handleMakeCall = useCallback((phoneNumber) => {
    if (window.RCAdapter && phoneNumber) {
      window.RCAdapter.setMinimized(false);
      window.RCAdapter.clickToCall(phoneNumber, true);
    } else if (!phoneNumber) {
      toast.error("No phone number available for this patient");
    }
  }, []);

  // Effect to automatically open RingCentral for monitoring time alert
  useEffect(() => {
    if (state.hasMonitoringTimeAlert) {
      const primaryNumber = getPrimaryPhoneNumber();
      if (primaryNumber) {
        // Small timeout to ensure UI is ready
        setTimeout(() => {
          handleMakeCall(primaryNumber);
        }, 300);
      }
      // Reset the flag after handling
      updateState({ hasMonitoringTimeAlert: false });
    }
  }, [state.hasMonitoringTimeAlert, getPrimaryPhoneNumber, handleMakeCall, updateState]);

  const generateAlerts = useCallback(
    async (patientData, patientId) => {
      const alerts = [];
      let hasMonitoringTimeAlert = false;

      if (patientData.last_out_of_range_record) {
        alerts.push(
          createOutOfRangeAlert(patientData.last_out_of_range_record)
        );
      }

      if (patientData.unseen_readings === true) {
        alerts.push(createUnseenReadingsAlert());
      }

      // Only show monitoring time alert if monitoring_session is true
      if (
        patientData.monitoring_session === true &&
        patientData.monitoring_time?.total_rpm_seconds <
        ALERT_CONFIG.THRESHOLDS.MIN_RPM_SECONDS &&
        patientData.rpm
      ) {
        alerts.push(createMonitoringTimeAlert(patientData.monitoring_time));
        hasMonitoringTimeAlert = true;
      }

      const reminderAlert = checkReadingReminder(patientData);
      if (reminderAlert) {
        alerts.push(reminderAlert);
      }

      // Update state with the monitoring time alert flag
      updateState({ hasMonitoringTimeAlert });

      return alerts;
    },
    [
      createOutOfRangeAlert,
      createUnseenReadingsAlert,
      createMonitoringTimeAlert,
      checkReadingReminder,
      updateState,
    ]
  );

  const handleChatClick = useCallback(
    (phoneNumber) => {
      updateState({
        alertModalOpen: false,
        isModalOpen: true,
        phoneNumber,
      });
    },
    [updateState]
  );

  const handleResolveClick = useCallback(async () => {
    try {
      updateState({ isLoading: true });
      const patientId = state.patient?.id || sessionStorage.getItem("_pt_id");

      if (!patientId) {
        toast.error("Patient ID not found");
        return;
      }

      const outOfRangeData = await getOutOfRangeReadings(patientId);

      if (outOfRangeData?.readings?.length > 0) {
        updateState({
          outOfRangeReadings: outOfRangeData.readings,
          alertModalOpen: false,
          outOfRangeModalOpen: true,
        });
      } else {
        toast.error("No out-of-range readings found");
      }
    } catch (error) {
      toast.error("Failed to fetch out-of-range readings");
    } finally {
      updateState({ isLoading: false });
    }
  }, [state.patient?.id, updateState]);

  const handleRemindPatient = useCallback(() => {
    const primaryNumber = getPrimaryPhoneNumber();

    if (!primaryNumber) {
      toast.error("No phone number available for this patient");
      return;
    }

    updateState({
      alertModalOpen: false,
      isModalOpen: true,
      phoneNumber: primaryNumber,
    });
  }, [getPrimaryPhoneNumber, updateState]);

  const handleReviewClick = useCallback(() => {
    updateState({ alertModalOpen: false });
  }, [updateState]);

  const fetchPatientDataAndGenerateAlerts = useCallback(async () => {
    try {
      updateState({ isLoading: true });
      const patientId = sessionStorage.getItem("_pt_id");

      if (!patientId) {
        return;
      }

      const patientData = await getPatientGeneralDetails(patientId);
      const alerts = await generateAlerts(patientData, patientId);

      updateState({
        patient: patientData,
        alerts,
      });
    } catch (error) {
      toast.error("Failed to fetch patient data");
      updateState({ alerts: [] });
    } finally {
      updateState({ isLoading: false });
    }
  }, [updateState, generateAlerts]);

  useEffect(() => {
    fetchPatientDataAndGenerateAlerts();
  }, [fetchPatientDataAndGenerateAlerts]);

  const alertsCount = useMemo(() => state.alerts.length, [state.alerts.length]);

  const getAlertActions = useCallback(
    (alert) => {
      if (alert.message === "Patient has no readings available.") {
        return null;
      }

      const actions = {
        [ALERT_CONFIG.TYPES.OUT_OF_RANGE]: (
          <button onClick={handleResolveClick} className={styles.resolveBtn}>
            Resolve
          </button>
        ),
        [ALERT_CONFIG.TYPES.UNSEEN_READINGS]: (
          <button onClick={handleReviewClick} className={styles.resolveBtn}>
            Review
          </button>
        ),
        [ALERT_CONFIG.TYPES.REMINDER]: (
          <button onClick={() => handleChatClick(patientDetails?.user?.primary_phone)} className={styles.resolveBtn}>
            Remind Patient
          </button>
        ),
        [ALERT_CONFIG.TYPES.MONITORING_TIME]: (
          <button
            onClick={() => {
              updateState({ alertModalOpen: false });
              handleMakeCall(patientDetails?.user?.primary_phone);
            }}
            className={styles.acknowledgeBtn}
          >
            Schedule
          </button>
        ),
      };

      return actions[alert.type] || null;
    },
    [
      handleResolveClick,
      handleReviewClick,
      handleRemindPatient,
      updateState,
      handleMakeCall,
      patientDetails,
    ]
  );

  return (
    <>

      {!isOpen && (
        <div className={styles.container_wrapper}>
          <div className={styles.phoneButtonWrapper}>
            <div className={styles.iconButton} onClick={onClose}>
              <FontAwesomeIcon icon={faChevronDown} className={styles.icon} />
            </div>
          </div>
        </div>
      )}

      <div className={`${styles.container} ${isOpen ? styles.open : ""}`}>
        { }
        <div className={styles.phoneButtonWrapper}>
          <div className={styles.iconButton}>
            <FontAwesomeIcon icon={faPhone} className={styles.icon} />
          </div>
          <div className={styles.PhonePopup}>
            <div
              className={styles.popupItem}
              onClick={() => handleMakeCall(patientDetails?.user?.primary_phone)}
            >
              <FontAwesomeIcon icon={faPhone} />
              <div>
                <p>Primary</p>
                <p className={styles.phone_number}>
                  {patientDetails?.user?.primary_phone}
                </p>
              </div>
            </div>
            {patientContacts
              ?.filter((_contact) => !_contact.is_primary)
              ?.map((phone) => (
                <div
                  key={phone.number}
                  className={styles.popupItem}
                  onClick={() => handleMakeCall(phone.number)}
                >
                  <FontAwesomeIcon icon={faPhone} />
                  <div>
                    <p>{phone.label}</p>
                    <p className={styles.phone_number}>({phone.number})</p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        { }
        <div className={styles.MessageButtonWrapper}>
          <div className={styles.iconButton}>
            <FontAwesomeIcon icon={faCommentDots} className={styles.icon} />
          </div>
          <div className={styles.MessagePopup}>
            <div
              className={styles.popupItem}
              onClick={() => handleChatClick(patientDetails?.user?.primary_phone)}
            >
              <FontAwesomeIcon icon={faPhone} />
              <div>
                <p>Primary</p>
                <p className={styles.phone_number}>
                  {patientDetails?.user?.primary_phone}
                </p>
              </div>
            </div>
            {patientContacts
              ?.filter((_contact) => !_contact.is_primary)
              ?.map((phone) => (
                <div
                  key={phone.number}
                  className={styles.popupItem}
                  onClick={() => handleChatClick(phone.number)}
                >
                  <FontAwesomeIcon icon={faPhone} />
                  <div>
                    <p>{phone.label}</p>
                    <p className={styles.phone_number}>({phone.number})</p>
                  </div>
                </div>
              ))}
          </div>
        </div>

        <div
          className={styles.NotificationButtonWrapper}
          onClick={() => updateState({ alertModalOpen: true })}
        >
          <div className={styles.iconButton}>
            <FontAwesomeIcon icon={faBell} className={styles.icon} />
            {alertsCount > 0 && (
              <span className={styles.badge}>{alertsCount}</span>
            )}
          </div>
        </div>
      </div>

      {state.isModalOpen && (
        <div className={styles.modalOverlay}>
          <div className={styles.modal}>
            <RingCentralWebSocket
              targetPhoneNumber={state.phoneNumber}
              setIsModalOpen={(isOpen) => updateState({ isModalOpen: isOpen })}
            />
          </div>
        </div>
      )}

      <Modal
        title="Patient Alerts"
        isOpen={state.alertModalOpen}
        onClose={() => updateState({ alertModalOpen: false })}
        width="800px"

      >
        <div className={styles.alertsContainer}>
          {state.isLoading ? (
            <div className={styles.loadingContainer}>
              <Spinner />
              <p className={styles.loadingText}>Loading patient alerts...</p>
            </div>
          ) : state.alerts.length === 0 ? (
            <div className={styles.noAlertsMessage}>
              <p>No alerts found for this patient.</p>
            </div>
          ) : (
            state.alerts.map((alert) => (
              <div
                key={alert.id}
                className={`${styles.alertItem} ${styles[`alertSeverity-${alert.severity}`]
                  }`}
              >
                <div className={styles.alertHeader}>
                  <span className={styles.alertHeaderText}>{alert.title}</span>
                </div>

                <div className={styles.alertContent}>
                  <div
                    className={`${styles.statusIndicator} ${styles[`${alert.severity}Indicator`]
                      }`}
                  >
                    <div className={styles.outerCircle}>
                      <div className={styles.innerCircle}></div>
                    </div>
                  </div>
                  <div className={styles.alertMessage}>
                    <div className={styles.messageText}>{alert.message}</div>
                    <div className={styles.timestamp}>
                      {alert.formattedDate}
                    </div>
                  </div>
                  {getAlertActions(alert)}
                </div>
              </div>
            ))
          )}
        </div>
      </Modal>

      {state.outOfRangeModalOpen && (
        <Modal
          title="Out of Range Readings"
          isOpen={state.outOfRangeModalOpen}
          onClose={() => updateState({ outOfRangeModalOpen: false })}
          width="900px"
        >
          <div className={styles.chartsContainer}>
            <Charts
              readings={state.outOfRangeReadings}
              showActionButtons={true}
              onStatusChange={(status, ids) => {
                const statusMessages = {
                  reviewed: `${ids.length} reading(s) marked as reviewed`,
                  escalated: `${ids.length} reading(s) escalated`,
                };

                toast.success(statusMessages[status]);
                updateState({ outOfRangeModalOpen: false });
                fetchPatientDataAndGenerateAlerts();
              }}
              onRefresh={fetchPatientDataAndGenerateAlerts}
            />
          </div>
        </Modal>
      )}
    </>
  );
};

export default Contact;